import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import {
  KNOWN_ORGANIZATIONS,
  QUPIL_ORGANIZATION_ID,
} from "../constants/global";

const STORAGE_KEY = "organizationId";

/**
 * Single source of truth for the organisation that super-admin operations
 * target. Wraps localStorage so screens reactively re-render when the org
 * changes, and forces Qupil as the default when no value is stored.
 *
 * QSL-9781 phase 1: read/write through the service replaces silent reads of
 * the bare ORGANIZATION_ID constant. Existing callers that import the
 * constant continue to work; new code (and the visible header banner) uses
 * the observable.
 */
@Injectable({ providedIn: "root" })
export class OrganizationContextService {
  private readonly _currentOrgId$ = new BehaviorSubject<string>(
    this.readOrInitialize()
  );

  readonly currentOrgId$: Observable<string> = this._currentOrgId$.asObservable();

  get currentOrgId(): string {
    return this._currentOrgId$.getValue();
  }

  get isQupil(): boolean {
    return this.currentOrgId === QUPIL_ORGANIZATION_ID;
  }

  /**
   * Resolves a friendly name for the given orgId from the known-orgs registry.
   * Returns "Unknown organisation" when the id is not registered, so an
   * unfamiliar id surfaces in the UI instead of being silently dropped.
   */
  getOrganizationName(orgId: string): string {
    return KNOWN_ORGANIZATIONS[orgId] ?? "Unknown organisation";
  }

  setOrganization(orgId: string): void {
    if (!orgId) return;
    localStorage.setItem(STORAGE_KEY, orgId);
    this._currentOrgId$.next(orgId);
  }

  resetToQupil(): void {
    this.setOrganization(QUPIL_ORGANIZATION_ID);
  }

  private readOrInitialize(): string {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored.trim().length > 0) {
      return stored;
    }
    localStorage.setItem(STORAGE_KEY, QUPIL_ORGANIZATION_ID);
    return QUPIL_ORGANIZATION_ID;
  }
}
